import React from 'react';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Table } from 'components/UI/V3/Table/Table';
import { TableHeader } from 'components/UI/V3/Table/Header/TableHeader';
import { TableHeaderCell } from 'components/UI/V3/Table/HeaderCell/TableHeaderCell';
import { TableBody } from 'components/UI/V3/Table/Body/TableBody';
import { TableRow } from 'components/UI/V3/Table/Row/TableRow';
import { TableCell } from 'components/UI/V3/Table/Cell/TableCell';
import ThreeDotsDropdown from 'components/UI/V3/ThreeDotsDropdown/ThreeDotsDropdown';
import { ThreeDotsDropdownOption } from 'components/UI/V3/ThreeDotsDropdown/ThreeDotsDropdownOption';
import { ServiceRate } from '@spike/boarding-rule-model';
import styles from './RateTable.module.scss';
import { faArrowDownLong, faArrowUpLong } from '@fortawesome/pro-regular-svg-icons';
import { RateTableRow } from './RateTableRow';

interface RateTableProps {
    serviceRates: ServiceRate[];
    isLoading: boolean;
    defaultPrice: number;
    getActionOptions: (serviceRate: ServiceRate) => ThreeDotsDropdownOption[];
}

const formatCurrency = (amount?: number): string => {
    if (amount === undefined || amount === null) return '—';
    return `$${amount.toFixed(2)}`;
};

const formatDate = (date: moment.Moment): string => {
    return moment.utc(date).format('ddd, D MMM YYYY');
};

export const RateTable: React.FC<RateTableProps> = ({ serviceRates, isLoading, defaultPrice, getActionOptions }) => {
    return (
        <>
            {/* Desktop table */}
            <Table
                tableClassName={styles.table}
                wrapperClassName={styles.tableWrapper}
            >
                <TableHeader>
                    <TableHeaderCell>Dates</TableHeaderCell>
                    <TableHeaderCell>
                        <div className={styles.rateContainer}>New Rate</div>
                    </TableHeaderCell>
                </TableHeader>
                <TableBody>
                    {isLoading ? (
                        <>
                            <TableRow>
                                <TableCell colSpan={3}>
                                    <div className={styles.skeletonRow} />
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell colSpan={3}>
                                    <div className={styles.skeletonRow} />
                                </TableCell>
                            </TableRow>
                        </>
                    ) : (
                        serviceRates.map((serviceRate, index) => (
                            <RateTableRow
                                key={`${serviceRate.rate.id}-${serviceRate.rateItem.id}-${index}`}
                                serviceRate={serviceRate}
                                defaultPrice={defaultPrice}
                                getActionOptions={getActionOptions}
                            />
                        ))
                    )}
                </TableBody>
            </Table>

            {/* Mobile list */}
            <div className={styles.mobileList}>
                {isLoading ? (
                    <>
                        <div className={styles.mobileSkeleton} />
                        <div className={styles.mobileSkeleton} />
                    </>
                ) : (
                    serviceRates.map((serviceRate, index) => {
                        const rateAmount = Number(serviceRate.rateItem.amount);
                        const isIncrease = rateAmount > defaultPrice;
                        const isDecrease = rateAmount < defaultPrice;

                        return (
                            <div
                                key={`mobile-${serviceRate.rate.id}-${serviceRate.rateItem.id}-${index}`}
                                className={styles.mobileItem}
                            >
                                <div className={styles.mobileItemHeader}>
                                    <span className={styles.mobileDates}>
                                        {formatDate(serviceRate.rate.startDate)} -{' '}
                                        {formatDate(serviceRate.rate.endDate)}
                                    </span>
                                    <div onClick={e => e.stopPropagation()}>
                                        <ThreeDotsDropdown
                                            options={getActionOptions(serviceRate)}
                                            direction="down"
                                        />
                                    </div>
                                </div>
                                <div className={styles.mobileItemRate}>
                                    <span className={styles.mobileRateLabel}>New Rate </span>
                                    <span className={styles.mobileRateValue}>{formatCurrency(rateAmount)}/Night</span>
                                    {isDecrease && (
                                        <FontAwesomeIcon
                                            icon={faArrowDownLong}
                                            className={styles.decreaseIcon}
                                        />
                                    )}
                                    {isIncrease && (
                                        <FontAwesomeIcon
                                            icon={faArrowUpLong}
                                            className={styles.increaseIcon}
                                        />
                                    )}
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        </>
    );
};
